/*=== CORE IMPORTS ===*/
import { html, LitElement } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { classMap } from 'lit/directives/class-map.js';
import { q1VersionHistoryStyles } from './q1-version-history-styles.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/details/details.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 COMPONENTS ===*/
import '../q1-member-chip/q1-member-chip.js';

/*=== Q1 MODULES ===*/
import { enrichVersion } from '../../modules/version-helpers-client.js';
import { q1VersionFunctions } from '../../modules/q1-core-functions.js';

/*=== CONSTANTS ===*/

// None yet.

/*=== COMPONENT CLASS ===*/

/**
 * Wrapper around the getVersionHistory function.
 * Queries and displays the most recent versions.
 * Note that there is not yet any pagination.
 * 
 * Designed to be used in signed in contexts only. Version history is not public.
 */
export class Q1VersionHistory extends LitElement {
  static styles = [ q1VersionHistoryStyles ];

  static properties = {
    /**
     * The title of the details component.
     * Defaults to 'Version History'.
     */
    title: { type: String, reflect: true },

    /** Defaults to `history`. */
    icon: { type: String, reflect: true },

    /** Defaults to `material`. */
    iconLibrary: { type: String, attribute: 'icon-library' },

    /**
     * Firestore ref path of the subject doc.
     * Example: `guildMembers/123456:987654`
     */
    subjectRef: { type: String, attribute: 'subject' },

    /**
     * Defaults to false. True when the version history is expanded.
     * When set to true upon initialization the component auto-queries the version history.
     * When set to false upon initialization the version history isn't queried until expanded.
     */
    open: { type: Boolean, reflect: true },

    /**
     * Optional map of all members by ref  which can be used to enrich the member ids in the list.
     * If not included, the members are displayed in the table as `[ Member Not Visible ]`
     */
    refPathMap: { type: Object },

    /**
     * Stores the queried version records.
     */
    versions: { type: Array },

    /**
     * The version stats object.
     */
    stats: { type: Object },

    /**
     * This tracks when the version history is being queried.
     */
    loading: { type: Boolean },
    
    /**
     * This goes to true once the initial refresh is complete if `open`, otherwise it goes to 
     * true right away.
     */
    initComplete: { type: Boolean },

    /**
     * Goes to true after the version history is loaded.
     */
    historyLoaded: { type: Boolean },

    error: { type: Boolean },
    errorMessage: { type: String },
  };

  constructor() {
    super();

    this.title = 'Version History';
    this.icon = 'history';
    this.iconLibrary = 'material';
    this.subjectRef = null;
    this.open = false;
    
    this.refPathMap = {};
    this.versions = [];
    this.stats = {};

    this.loading = false;
    this.initComplete = false;
    this.historyLoaded = false;

    this.error = false;
    this.errorMessage = '';
  }

  /*=== LIFECYCLE ===*/

  connectedCallback() {
    super.connectedCallback();

    if (!this.open) this.initComplete = true;
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      this.loadHistoryIfNeeded();
    }
  }

  /*=== ACTIONS ===*/

  /** Loads history if the component is open and history is not loaded. */
  async loadHistoryIfNeeded() {
    if (!this.open || this.historyLoaded) return;

    await this.loadHistory();
  }

  /** Reloads history and overwrites the current values. */
  async loadHistory() {
    const { getVersionHistory } = q1VersionFunctions;

    if (this.loading) return; // Prevent multiple loads
    this.loading = true;
    this.error = false;
    this.errorMessage = '';

    try {
      const response = await getVersionHistory({
        subject: this.subjectRef,
      });
      const { recentVersions, recentVersionStats } = response.data;

      let currentVersionFound = false;
      recentVersions.forEach(version => {
        enrichVersion(version, this.refPathMap);

        // Flag the first adopted version as current.
        // Note 1: Recent versions is sorted reverse chronological on the backend.
        // Note 2: Only one version should be adopted at a time, this is a safe guard for bad data
        if (!currentVersionFound && version.status === 'adopted') {
          currentVersionFound = true;
          version.isCurrent = true;
        }
      });

      this.versions = recentVersions;
      this.stats = recentVersionStats;
      this.historyLoaded = true;
    } catch (error) {
      console.error('Error loading version history', error);

      this.error = true;
      this.errorMessage = error.message;
    } finally {
      this.loading = false;
      this.initComplete = true;
    }
  }

  async loadMore(event) {
    this.refreshMemberships(event, true);
  }

  /*=== HANDLERS ===*/

  handleDetailsShow(event) {
    if (event.target.tagName !== 'SL-DETAILS') return; // sl-tooltip also throws this event
    this.open = true;
  }

  handleDetailsHide(event) {
    if (event.target.tagName !== 'SL-DETAILS') return; // sl-tooltip also throws this event
    this.open = false;
  }

  /*=== GETTERS ===*/

  // None yet

  /*=== TEMPLATES ===*/

  render() {
    return html`
      <div id="base-container" part="base">
        <sl-details
          part="details"
          ?open=${this.open}
          @sl-show=${this.handleDetailsShow}
          @sl-hide=${this.handleDetailsHide}
        >
          <span slot="summary">
            ${this.loading && this.initComplete ? html`
              <sl-spinner part="spinner"></sl-spinner>
            ` : html`
              <sl-icon library=${this.iconLibrary} name=${this.icon}></sl-icon>
            `}
            ${this.title}
          </span>

          <table-wrapper>
            ${this.versionTableTemplate}
          </table-wrapper>
        </sl-details>
      </div>
    `;
  }

  get versionTableTemplate() {
    return html`
      <table id="versions-table" class="q1">
        <thead>
          <th>Version</th>
          <th>Status</th>
          <th>Proposed</th>
        </thead>

        <tbody>
          ${this.error ? html`
            <tr class="empty-placeholder error">
              <td colspan="4">
                <span class="td-inner">
                  <sl-icon library="material" name="error"></sl-icon>
                  <span>${this.errorMessage}</span>
                </span>
              </td>
            </tr>
          ` : html`

            ${!this.versions?.length ? html`
              <tr class="empty-placeholder">
                <td colspan="4">
                  <span class="td-inner">
                    ${this.initComplete && !this.loading ? html`
                      <sl-icon library="material" name="block"></sl-icon>
                      <span>This is no version history for this record yet.</span>
                    ` : html`
                      <sl-spinner part="spinner"></sl-spinner>
                      <span>Loading...</span>
                    `}
                  </span>
                </td>
              </tr>
            ` : repeat(this.versions, (version) => version.id, (version, index) => html`
              <tr class=${classMap({
                'current-version': version.isCurrent,
                [version.approvalStatus]: true,
              })}>
                <td class="title-col">
                  <span class="td-inner">
                    <a href=${version.detailViewPath} class="title-link">
                      <sl-icon
                        library=${version.approvalFlow_iconLibrary}
                        name=${version.approvalFlow_icon}
                      ></sl-icon> ${version.approvalFlow_label} • v${version.versionNumber}
                    </a>
                    ${!version.isCurrent ? '' : html`
                      <sl-tooltip content="Current Version">
                        <sl-icon library="material" name="verified"></sl-icon>
                      </sl-tooltip>
                    `}
                  </span>
                </td>

                <td class="status-col">
                  <span class="td-inner">
                    <sl-tooltip content=${version.approvalStatus_label}>
                      <sl-icon
                        class=${version.approvalStatus}
                        library=${version.approvalStatus_iconLibrary}
                        name=${version.approvalStatus_icon}
                      ></sl-icon>
                    </sl-tooltip>

                    <span class="divider">/</span>

                    <sl-tooltip content=${version.status_label}>
                      <sl-icon
                        class=${version.status}
                        library=${version.status_iconLibrary}
                        name=${version.status_icon}
                      ></sl-icon>
                    </sl-tooltip>
                  </span>
                </td>

                <td class="proposed-col">
                  <span class="td-inner">
                    ${!version.proposedAt ? '-' : html`
                      <sl-format-date
                        date=${version.proposedAt}
                        month="short" day="numeric" year="numeric"
                      ></sl-format-date>
                    `}
                    ${!version.proposer_member ? '' : html`
                      <span>by</span>
                      <q1-member-chip
                        .member=${version.proposer_member}
                        no-links signed-in
                      ></q1-member-chip>
                    `}
                </td>
              </tr>
            `)}

          `}
        </tbody>
      </table>
    `;
  }
  
}

customElements.define('q1-version-history', Q1VersionHistory);
